import { ResizeSensor } from '@app/utils/resize-sensor';
import $ from 'jquery';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, shareReplay, startWith } from 'rxjs/operators';

export class Viewport {
  size$: Observable<string>;

  private $element: JQuery;
  private keys: string[];
  private resize$: Subject<number>;
  private resizeCallback: (value: { height: number; width: number }) => void;
  private resizeSensor: ResizeSensor;
  private width: number;

  get currentSize(): string {
    return this.getCurrentSize();
  }

  get sizes() {
    return this._sizes;
  }

  constructor(
    private _sizes: { [key: string]: number[] },
    private element: HTMLElement = document.body,
    private debounce: number = 100
  ) {
    this.$element = $(element);
    this.keys = Object.keys(_sizes);
    this.width = this.$element.width();

    this.resize$ = new BehaviorSubject<number>(this.element.offsetWidth);
    this.size$ = this.resize$.pipe(
      debounceTime(this.debounce),
      map(() => this.getCurrentSize()),
      startWith(this.getCurrentSize()),
      distinctUntilChanged(),
      shareReplay(1)
    );

    this.resizeCallback = (value) => {
      this.resize$.next(value.width);
    };

    this.resizeSensor = new ResizeSensor(this.element, this.resizeCallback, debounce);
  }

  cleanup() {
    this.resizeSensor.detach(this.resizeCallback);
  }

  is(size, orGreater: boolean = false): boolean {
    let returnValue = false;
    const [min, max] = this._sizes[size];

    // Check the element for width
    const elementWidth = $(this.element).innerWidth();

    if (orGreater) {
      returnValue = elementWidth >= min;
    } else {
      returnValue = elementWidth >= min && elementWidth <= max;
    }

    return returnValue;
  }

  isDesktop(): boolean {
    return this.is('lg') || this.is('xl');
  }

  isMobile(): boolean {
    return this.is('xs') || this.is('sm');
  }

  isTablet(): boolean {
    return this.is('md') || this.is('lg');
  }

  private getCurrentSize() {
    const sizeMap: [string, boolean][] = this.keys.map((size) => [size, this.is(size)]);
    const currentSize = sizeMap.find(([, matches]) => matches === true);

    return currentSize?.[0] ?? null;
  }
}

export const BootstrapViewport = new Viewport({
  xs: [0, 575],
  sm: [576, 767],
  md: [768, 991],
  lg: [992, 1199],
  xl: [1200, Infinity],
});
